import { ActionTree } from 'vuex';
import api from '@/api';
import i18next from '@/i18n';
import router from '@/router';
import { RootState, RootActions, ErrorMessages, ListStatus, EditableStatus } from '@/store/types';
import {
  DistributorsState,
  DistributorsMutations,
  DistributorsActions,
} from '@/store/modules/distributors/types';
import Distributor, { DistributorJSON, DistributorStatus } from '@/store/modules/distributors/distributor';
import { NotificationsActions } from '@/store/modules/notifications/types';
import { enhanceErrorWithMessage } from '@/utils/errors';

export const actions: ActionTree<DistributorsState, RootState> = {
  [DistributorsActions.LOAD_LIST]({ commit, dispatch }, filters = {}): Promise<void> {
    commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Loading);

    return api.distributors
      .list(filters)
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        commit(
          DistributorsMutations.SET_LIST,
          rows.map((item) => Distributor.fromJSON(item)),
        );
        commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [DistributorsActions.LOAD_DISTRIBUTOR]({ commit, dispatch }, { id } = {}): Promise<void> {
    commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Loading);

    const numericId = id != null && id !== '' ? Number(id) : NaN;

    if (!id || !Number.isFinite(numericId)) {
      const empty = new Distributor();
      empty.status = DistributorStatus.Active;
      commit(DistributorsMutations.SET_DISTRIBUTOR, empty);
      commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      return Promise.resolve();
    }

    return api.distributors
      .get(numericId)
      .then((response) => {
        commit(DistributorsMutations.SET_DISTRIBUTOR, Distributor.fromJSON(response.data));
        commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [DistributorsActions.SAVE_EDITABLE]({ dispatch, commit, state }): Promise<void> {
    commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);

    if (!state.editableDistributor) {
      return Promise.reject(new Error('Internal Error: Editable distributor not available.'));
    }

    const payload: DistributorJSON = state.editableDistributor.toJSON();
    const isUpdate = Boolean(payload.id);
    let successMsg = i18next.t('A new distributor was added successfully.');

    if (isUpdate) {
      successMsg = i18next.t('The distributor was successfully updated.');
    }

    return api.distributors
      .store(payload)
      .then((response): Promise<Distributor> => {
        const saved = Distributor.fromJSON(response.data);
        commit(DistributorsMutations.SET_DISTRIBUTOR, saved);
        commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);

        if (!isUpdate) {
          return new Promise<Distributor>((resolve, reject) => {
            if (!saved.id) {
              return reject(new Error('Invalid data.'));
            }
            router.replace(
              {
                name: 'edit-distributor',
                params: {
                  id: String(saved.id),
                },
              },
              () => resolve(saved),
              (error: unknown) => reject(error),
            );
          });
        }

        return Promise.resolve(saved);
      })
      .then((saved: Distributor) => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: successMsg,
            visit: {
              link: {
                name: 'distributors',
              },
              label: i18next.t('View distributors'),
            },
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DISTRIBUTOR_SAVE_ERROR);
        commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [DistributorsActions.UPDATE_EDITABLE]({ commit }, changes): Promise<void> {
    commit(DistributorsMutations.UPDATE_EDITABLE, changes);
    return Promise.resolve();
  },

  [DistributorsActions.DELETE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-distributor';
    if (isEdit) {
      commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }
      resolve();
    })
      .then(() => api.distributors.remove(id))
      .then(() => {
        if (isEdit) {
          commit(DistributorsMutations.UPDATE_INITIAL_EDITABLE, {
            status: 0,
          });
          commit(DistributorsMutations.UPDATE_EDITABLE, {
            status: 0,
          });
          commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(DistributorsActions.LOAD_LIST, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() =>
        dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The distributor was deleted successfully.'),
          },
          { root: true },
        ),
      )
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Failed);
        }

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [DistributorsActions.RESTORE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-distributor';
    if (isEdit) {
      commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }
      resolve();
    })
      .then(() => api.distributors.restore(id))
      .then(() => {
        if (isEdit) {
          commit(DistributorsMutations.UPDATE_INITIAL_EDITABLE, {
            status: 1,
          });
          commit(DistributorsMutations.UPDATE_EDITABLE, {
            status: 1,
          });
          commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(DistributorsActions.LOAD_LIST, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() =>
        dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The distributor was restored successfully.'),
          },
          { root: true },
        ),
      )
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(DistributorsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(DistributorsMutations.SET_LIST_STATUS, ListStatus.Failed);
        }

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
};

export default actions;
